import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { getApiUrl } from "../../../../api";
import PostForm from "./PostForm";
import ImageUploadModal from "../../../../components/modal/ImageUploadModal";

interface ContentSection {
    id: number;
    type?: string;
    subtitle: string;
    content?: string;
    tableData?: any;
}

interface DateFields { year: string; month: string; day: string; }

interface FormData {
    title: string;
    thumbnailImage: File | null;
    thumbnailImagePreview: string;
    popupContent: string;
    contentImage: File | null;
    contentImagePreview: string;
    imageDescription: string;
    isDecorative: boolean;
    contentSections: ContentSection[];
    parentMenu: string;
    startDate: DateFields;
    endDate: DateFields;
}

const buildBody = (sections: ContentSection[], dividers: Set<number>) => {
    const result: any[] = [];
    sections.forEach((section, index) => {
        if (section.type === "table") {
            const { headers, rows, columnKeys, tableType } = section.tableData;
            const columns = headers.map((label: string, i: number) => ({ key: columnKeys?.[i] || `col${i+1}`, label }));
            const value = rows.map((row: any) => {
                const obj: any = {};
                columns.forEach((col: any, ci: number) => {
                    const cellVal = row.cells[ci]?.value;
                    obj[col.key] = typeof cellVal === "boolean" ? (cellVal ? "O" : "X") : (cellVal ?? "");
                });
                return obj;
            });
            result.push({ type: "table", table_type: tableType, label: section.subtitle || "", columns, value });
        } else {
            result.push({ type: "text", label: section.subtitle || "", value: section.content || "" });
        }
        if (dividers.has(index) && index < sections.length - 1) result.push({ type: "divider" });
    });
    return result;
};

export default function PostRegister() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const kioskCode = searchParams.get("kiosk_code") || localStorage.getItem("kiosk_code") || "";
    const categoryCode = searchParams.get("category_code");
    const [dividerSet, setDividerSet] = useState(new Set<number>());
    const [isLoading, setIsLoading] = useState(false);
    const [resultModal, setResultModal] = useState({ open: false, title: "", description: "", success: false });
    const [imageModal, setImageModal] = useState({ open: false, targetField: "" });
    const [categoryList, setCategoryList] = useState<{ category_code: string; name: string }[]>([]);

    const [formData, setFormData] = useState<FormData>({
        title: "", thumbnailImage: null, thumbnailImagePreview: "",
        popupContent: "", contentImage: null, contentImagePreview: "",
        imageDescription: "", isDecorative: false,
        contentSections: [{ id: 1, subtitle: "", content: "" }],
        parentMenu: categoryCode || "",
        startDate: { year: "", month: "", day: "" },
        endDate: { year: "", month: "", day: "" }
    });

const location = useLocation();
    const getCmsToken = () => localStorage.getItem("cms_access_token");

    // 페이지에서 넘어온 state 처리
    useEffect(() => {
        const state = location.state as { title?: string; sections?: any[]; firstImageUrl?: string } | null;
        if (!state) return;
        if (state.title) setFormData(prev => ({ ...prev, title: state.title! }));
        if (state.sections?.length) setFormData(prev => ({ ...prev, contentSections: state.sections! }));
        if (state.firstImageUrl) setFormData(prev => ({ ...prev, contentImagePreview: state.firstImageUrl! }));
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            if (!kioskCode) return;
            try {
                const res = await fetch(getApiUrl(`/api/solutions/by-kiosk/${kioskCode}/categories`), {
                    headers: { Authorization: `Bearer ${getCmsToken()}` }
                });
                if (res.ok) setCategoryList(await res.json());
            } catch (e) { console.error(e); }
        };
        fetchCategories();
    }, [kioskCode]);

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => {
            const next = { ...prev, [field]: value };
            if (field === "imageDescription" && value.trim()) next.isDecorative = false;
            return next;
        });
    };

    const handleImageSelect = (field: string, previewField: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) setFormData(prev => ({ ...prev, [field]: file, [previewField]: URL.createObjectURL(file) }));
        e.target.value = "";
    };

    const handleOpenImageModal = (field: string) => setImageModal({ open: true, targetField: field });

    const handleRemoveImage = (field: string, previewField: string) =>
        setFormData(prev => ({ ...prev, [field]: null, [previewField]: "" }));

    const addContentSection = () => {
        setFormData(prev => ({ ...prev, contentSections: [...prev.contentSections, { id: Date.now(), subtitle: "", content: "" }] }));
        setDividerSet(d => { const next = new Set(d); next.add(formData.contentSections.length - 1); return next; });
    };

    const removeContentSection = (id: number) =>
        setFormData(prev => ({ ...prev, contentSections: prev.contentSections.filter(s => s.id !== id) }));

    const handleSectionChange = (id: number, field: string, value: any) =>
        setFormData(prev => ({ ...prev, contentSections: prev.contentSections.map(s => s.id !== id ? s : { ...s, [field]: value }) }));

    const handleToggleDivider = (gapIndex: number) => {
        setDividerSet(prev => { const next = new Set(prev); next.has(gapIndex) ? next.delete(gapIndex) : next.add(gapIndex); return next; });
    };

    const handleDateChange = (dateType: string, field: string, value: string) =>
        setFormData(prev => ({ ...prev, [dateType]: { ...(prev as any)[dateType], [field]: value } }));

    const handleMoveSection = (index: number, direction: "up" | "down") => {
        setFormData(prev => {
            const sections = [...prev.contentSections];
            const targetIndex = direction === "up" ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= sections.length) return prev;
            [sections[index], sections[targetIndex]] = [sections[targetIndex], sections[index]];
            return { ...prev, contentSections: sections };
        });
    };

    const handleSectionsReorder = (newSections: ContentSection[]) =>
        setFormData(prev => ({ ...prev, contentSections: newSections }));

    const handleAddTable = (tableType: string) => {
        const newSection: ContentSection = {
            id: Date.now(), type: "table", subtitle: "",
            tableData: {
                tableType, headers: ["열1", "열2", "열3"],
                rows: Array.from({ length: 3 }, () => ({
                    cells: Array.from({ length: 3 }, (_, ci) => ({
                        cellType: tableType === "check" && ci > 0 ? "checkbox" : "text",
                        value: tableType === "check" && ci > 0 ? "X" : "",
                    }))
                }))
            }
        };
        setFormData(prev => ({ ...prev, contentSections: [...prev.contentSections, newSection] }));
    };

    const handleTableHeaderChange = (sectionId: number, target: string, value: string, colIdx?: number) => {
        setFormData(prev => ({
            ...prev,
            contentSections: prev.contentSections.map(s => {
                if (s.id !== sectionId) return s;
                if (target === "subtitle") return { ...s, subtitle: value };
                if (target === "header" && colIdx !== undefined) {
                    const headers = [...s.tableData.headers]; headers[colIdx] = value;
                    return { ...s, tableData: { ...s.tableData, headers } };
                }
                return s;
            })
        }));
    };

    const handleTableCellChange = (sectionId: number, rIdx: number, cIdx: number, value: any) => {
        setFormData(prev => ({
            ...prev,
            contentSections: prev.contentSections.map(s => {
                if (s.id !== sectionId) return s;
                const rows = s.tableData.rows.map((row: any, r: number) =>
                    r !== rIdx ? row : { cells: row.cells.map((c: any, ci: number) => ci !== cIdx ? c : { ...c, value }) }
                );
                return { ...s, tableData: { ...s.tableData, rows } };
            })
        }));
    };

    const handleTableAddRow = (sectionId: number) => {
        setFormData(prev => ({
            ...prev,
            contentSections: prev.contentSections.map(s => {
                if (s.id !== sectionId) return s;
                const newRow = { cells: s.tableData.headers.map((_: any, ci: number) => ({ cellType: s.tableData.tableType === "check" && ci > 0 ? "checkbox" : "text", value: s.tableData.tableType === "check" && ci > 0 ? "X" : "" })) };
                return { ...s, tableData: { ...s.tableData, rows: [...s.tableData.rows, newRow] } };
            })
        }));
    };

    const handleTableRemoveRow = (sectionId: number, rIdx: number) => {
        setFormData(prev => ({
            ...prev,
            contentSections: prev.contentSections.map(s => {
                if (s.id !== sectionId) return s;
                return { ...s, tableData: { ...s.tableData, rows: s.tableData.rows.filter((_: any, i: number) => i !== rIdx) } };
            })
        }));
    };

    const MAX_COLS: Record<string, number> = { check: 6, text: 4 };

    const handleTableAddCol = (sectionId: number) => {
        setFormData(prev => ({
            ...prev,
            contentSections: prev.contentSections.map(s => {
                if (s.id !== sectionId) return s;
                if (s.tableData.headers.length >= (MAX_COLS[s.tableData.tableType] ?? 4)) return s;
                const newCellType = s.tableData.tableType === "check" ? "checkbox" : "text";
                const headers = [...s.tableData.headers, `열${s.tableData.headers.length + 1}`];
                const columnKeys = [...(s.tableData.columnKeys || []), `col${s.tableData.headers.length + 1}`];
                const rows = s.tableData.rows.map((row: any) => ({ cells: [...row.cells, { cellType: newCellType, value: newCellType === "checkbox" ? "X" : "" }] }));
                return { ...s, tableData: { ...s.tableData, headers, columnKeys, rows } };
            })
        }));
    };

    const handleTableRemoveCol = (sectionId: number) => {
        setFormData(prev => ({
            ...prev,
            contentSections: prev.contentSections.map(s => {
                if (s.id !== sectionId || s.tableData.headers.length <= 1) return s;
                const headers = s.tableData.headers.slice(0, -1);
                const columnKeys = (s.tableData.columnKeys || []).slice(0, -1);
                const rows = s.tableData.rows.map((row: any) => ({ cells: row.cells.slice(0, -1) }));
                return { ...s, tableData: { ...s.tableData, headers, columnKeys, rows } };
            })
        }));
    };

    const handleSubmit = async () => {
        if (!formData.title.trim()) return setResultModal({ open: true, title: "컨텐츠명을 입력해주세요.", description: "", success: false });
        if (!formData.parentMenu) return setResultModal({ open: true, title: "상위 메뉴를 선택해주세요.", description: "", success: false });

        // 이미지 설명 검증 (설명없음 불가, 반드시 설명 입력)
        // 이미지만 있고 섹션 내용이 전부 비어있으면 이미지 설명 필수
        const textSections = formData.contentSections.filter(s => s.type !== "table");
        const allSectionsEmpty = textSections.every(s => !s.content?.trim() && !s.subtitle?.trim());
        if (formData.contentImagePreview) {
            if (allSectionsEmpty && !formData.imageDescription.trim()) {
                return setResultModal({ open: true, title: "이미지 설명을 작성해주세요.", description: "", success: false });
            }
            if (!allSectionsEmpty && !formData.isDecorative && !formData.imageDescription.trim()) {
                return setResultModal({ open: true, title: "이미지 설명을 작성하거나 설명없음을 체크해주세요.", description: "", success: false });
            }
        }

        // 섹션 검증
        const hasEmptySection = !allSectionsEmpty && textSections.some(s => !s.content?.trim() && !s.subtitle?.trim());
        const hasEmptyContent = textSections.some(s => s.subtitle?.trim() && !s.content?.trim());
        const hasEmptySubtitle = textSections.some(s => s.content?.trim() && !s.subtitle?.trim());
        if (hasEmptySection) return setResultModal({ open: true, title: "소제목과 내용을 작성해주세요.", description: "", success: false })
        if (hasEmptyContent) return setResultModal({ open: true, title: "모든 내용칸을 작성해주세요.", description: "", success: false });
        if (hasEmptySubtitle) return setResultModal({ open: true, title: "모든 소제목을 작성해주세요.", description: "", success: false });

        setIsLoading(true);
        try {
            const token = getCmsToken();
            let thumbnailUrl = null;
            let contentImageUrl = null;

            if (formData.thumbnailImage) {
                const fd = new FormData(); fd.append("file", formData.thumbnailImage);
                const res = await fetch(getApiUrl("/api/images/upload"), { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
                if (res.ok) thumbnailUrl = (await res.json()).url;
                else { setResultModal({ open: true, title: "썸네일 업로드 실패", description: "", success: false }); setIsLoading(false); return; }
            } else if (formData.thumbnailImagePreview) {
                thumbnailUrl = formData.thumbnailImagePreview;
            }

            if (formData.contentImage) {
                const fd = new FormData(); fd.append("file", formData.contentImage);
                const res = await fetch(getApiUrl("/api/images/upload"), { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
                if (res.ok) contentImageUrl = (await res.json()).url;
                else { setResultModal({ open: true, title: "이미지 업로드 실패", description: "", success: false }); setIsLoading(false); return; }
            } else if (formData.contentImagePreview) {
                contentImageUrl = formData.contentImagePreview;
            }

            const hasStart = formData.startDate.year && formData.startDate.month && formData.startDate.day;
            const hasEnd = formData.endDate.year && formData.endDate.month && formData.endDate.day;

            const res = await fetch(getApiUrl("/api/contents/"), {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                    title: formData.title,
                    body: buildBody(formData.contentSections, dividerSet),
                    image_url: contentImageUrl,
                    image_description: contentImageUrl && !formData.isDecorative ? formData.imageDescription : "",
                    is_decorative: contentImageUrl ? formData.isDecorative : false,
                    thumbnail_url: thumbnailUrl,
                    top_popup_message: formData.popupContent || null,
                    visible_start_at: hasStart ? `${formData.startDate.year}-${formData.startDate.month.padStart(2,"0")}-${formData.startDate.day.padStart(2,"0")}T00:00:00` : null,
                    visible_end_at: hasEnd ? `${formData.endDate.year}-${formData.endDate.month.padStart(2,"0")}-${formData.endDate.day.padStart(2,"0")}T23:59:59` : null,
                    category_code: formData.parentMenu,
                    image_url_list: []
                })
            });

            if (res.ok) setResultModal({ open: true, title: "컨텐츠가 정상적으로 등록되었습니다.", description: "", success: true });
            else { const err = await res.json(); setResultModal({ open: true, title: "등록 실패", description: err.detail || "", success: false }); }
        } catch { setResultModal({ open: true, title: "등록 중 오류가 발생했습니다.", description: "", success: false }); }
        finally { setIsLoading(false); }
    };

    return (
        <>
            <PostForm
                formData={formData}
                onInputChange={handleInputChange}
                onImageSelect={handleImageSelect}
                onAddContentSection={addContentSection}
                onRemoveContentSection={removeContentSection}
                onSectionChange={handleSectionChange}
                dividerSet={dividerSet}
                onToggleDivider={handleToggleDivider}
                onDateChange={handleDateChange}
                categoryList={categoryList}
                onSubmit={handleSubmit}
                onCancel={() => navigate(-1)}
                mode="create"
                isLoading={isLoading}
                onMoveSection={handleMoveSection}
                onAddTable={handleAddTable}
                onTableHeaderChange={handleTableHeaderChange}
                onTableCellChange={handleTableCellChange}
                onTableAddRow={handleTableAddRow}
                onTableRemoveRow={handleTableRemoveRow}
                onTableAddCol={handleTableAddCol}
                onTableRemoveCol={handleTableRemoveCol}
                onSectionsReorder={handleSectionsReorder}
                onOpenImageModal={handleOpenImageModal}
                onRemoveImage={handleRemoveImage}
            />

            {resultModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
                        <p className="text-[14px] font-semibold text-[#2a1f1a] mb-1">{resultModal.title}</p>
                        {resultModal.description && <p className="text-[12px] text-gray-400 mb-4">{resultModal.description}</p>}
                        <button onClick={() => { setResultModal(prev => ({ ...prev, open: false })); if (resultModal.success) navigate(`/admin/kiosk/content?kiosk_code=${kioskCode}&category_code=${categoryCode || formData.parentMenu}`); }}
                            className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold mt-2">확인</button>
                    </div>
                </div>
            )}

            <ImageUploadModal
                isOpen={imageModal.open}
                onClose={() => setImageModal({ open: false, targetField: "" })}
                onSelectFile={(file) => {
                    const previewField = imageModal.targetField + "Preview";
                    setFormData(prev => ({
                        ...prev,
                        [imageModal.targetField]: file,
                        [previewField]: URL.createObjectURL(file)
                    }));
                    setImageModal({ open: false, targetField: "" });
                }}
            />
        </>

        
    );
}